import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Shield, Users, MessageCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

const CommunityGuidelines = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
      <div className="container mx-auto px-4 py-8">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="max-w-4xl mx-auto bg-card rounded-lg shadow-lg p-8 mb-8">
          <header className="mb-8 border-b pb-6">
            <h1 className="text-4xl font-bold mb-4">Community Guidelines</h1>
            <p className="text-muted-foreground">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </header>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <div className="bg-primary/10 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-semibold mb-4">Our Community Values</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Her Season's community is built on trust, empathy, and mutual support. We're here to help each other navigate the journey through perimenopause and menopause with grace, humor, and understanding. These guidelines ensure our community remains a safe, welcoming space for all members.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Heart className="h-6 w-6 text-primary" />
                1. Be Kind and Respectful
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We're all going through similar experiences, but everyone's journey is unique. Please:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Treat others with empathy, compassion, and respect</li>
                <li>Assume good intentions in others' posts and comments</li>
                <li>Acknowledge that what works for you may not work for everyone</li>
                <li>Use inclusive language that welcomes all members</li>
                <li>Celebrate others' victories and support them through challenges</li>
                <li>Disagree respectfully without personal attacks</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                2. Protect Privacy and Safety
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Your safety and privacy are paramount. To maintain a secure community:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Never share:</strong> Full names, addresses, phone numbers, or other personal identifying information (yours or others')</li>
                <li><strong>Be cautious with:</strong> Photos that could identify you or your location</li>
                <li><strong>Respect confidentiality:</strong> What's shared in our community stays in our community</li>
                <li><strong>Report concerns:</strong> If you see something that makes you uncomfortable, report it to our moderators</li>
                <li><strong>No screenshots:</strong> Don't share screenshots of community posts on other platforms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-primary" />
                3. Medical Information Guidelines
              </h2>
              <div className="bg-destructive/10 border-l-4 border-destructive p-4 my-4">
                <p className="text-muted-foreground leading-relaxed font-semibold">
                  IMPORTANT: Our community is for peer support, NOT medical advice.
                </p>
              </div>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Share experiences:</strong> You can share what has worked for you personally</li>
                <li><strong>Don't diagnose:</strong> Never diagnose others or suggest specific treatments</li>
                <li><strong>Encourage professional help:</strong> Always recommend consulting healthcare providers</li>
                <li><strong>No medical credentials required:</strong> You don't need to be a healthcare professional to participate</li>
                <li><strong>Disclaimer your experience:</strong> Make it clear when you're sharing personal experience vs. medical fact</li>
                <li><strong>Emergency situations:</strong> If someone expresses thoughts of self-harm, urge them to contact emergency services immediately</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Users className="h-6 w-6 text-primary" />
                4. Create Quality Content
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Help keep our community valuable and engaging by:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Use clear titles:</strong> Help others find relevant discussions</li>
                <li><strong>Stay on topic:</strong> Keep posts related to perimenopause, menopause, and women's health</li>
                <li><strong>Search first:</strong> Check if your question has been asked before</li>
                <li><strong>Provide context:</strong> Give enough detail for meaningful responses</li>
                <li><strong>Update your posts:</strong> Come back to share how things turned out</li>
                <li><strong>Use appropriate tags:</strong> Help organize content by topic</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <MessageCircle className="h-6 w-6 text-primary" />
                5. Engage Authentically
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Build genuine connections by:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Being honest about your experiences</li>
                <li>Offering support without judgment</li>
                <li>Contributing constructively to discussions</li>
                <li>Acknowledging when you don't know something</li>
                <li>Being patient with members who are new to the community</li>
                <li>Using humor appropriately and sensitively</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Prohibited Content and Behavior</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The following are strictly prohibited and may result in immediate removal and account suspension:
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">Harassment and Hate</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Bullying, threatening, or intimidating others</li>
                <li>Hate speech or discrimination based on race, ethnicity, religion, gender, sexual orientation, disability, or age</li>
                <li>Sexual harassment or unwanted sexual content</li>
                <li>Doxxing or sharing others' private information</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Misinformation and Spam</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Spreading false or misleading health information</li>
                <li>Promoting dangerous or unproven treatments</li>
                <li>Spam, excessive self-promotion, or commercial solicitation</li>
                <li>Multi-level marketing (MLM) or pyramid schemes</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Illegal and Harmful Content</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Illegal activities or content</li>
                <li>Graphic violence or disturbing imagery</li>
                <li>Content encouraging self-harm or eating disorders</li>
                <li>Impersonation of others</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Moderation and Enforcement</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">Our Approach</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Our moderation team works to keep the community safe while respecting free expression. We:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Review reported content promptly</li>
                <li>Remove content that violates guidelines</li>
                <li>Issue warnings for first-time minor violations</li>
                <li>Apply temporary or permanent bans for serious or repeated violations</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Consequences for Violations</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>First offense (minor):</strong> Warning and content removal</li>
                <li><strong>Second offense:</strong> Temporary suspension (3-7 days)</li>
                <li><strong>Serious violations:</strong> Immediate permanent ban</li>
                <li><strong>Repeated violations:</strong> Permanent ban</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Reporting Content</h3>
              <p className="text-muted-foreground leading-relaxed">
                If you see content that violates these guidelines, please report it using the report button on any post or comment. Our team reviews all reports confidentially. False or malicious reports may result in action against the reporter's account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Appeals Process</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you believe your content was removed or your account was suspended in error, you may appeal by contacting us at community@auralife.com within 30 days. Please include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                <li>Your username</li>
                <li>Details about the removed content or suspension</li>
                <li>Explanation of why you believe the action was in error</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We will review appeals within 5 business days. Our decision after review is final.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Changes to Guidelines</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update these Community Guidelines from time to time to reflect community needs and feedback. We will notify members of significant changes. Continued participation in the community constitutes acceptance of updated guidelines.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Building Together</h2>
              <p className="text-muted-foreground leading-relaxed">
                These guidelines exist to protect and nurture our community, but they're just a framework. The real magic happens when we all commit to showing up with kindness, authenticity, and support for one another. Thank you for being part of Her Season and helping create a space where women can thrive through every stage of this journey.
              </p>
            </section>

            <section className="bg-primary/5 rounded-lg p-6 mt-8">
              <h2 className="text-2xl font-semibold mb-4">Questions or Concerns?</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about these guidelines or need to report a concern, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-background rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Community Team</strong><br />
                  Email: community@auralife.com<br />
                  Response time: Within 24 hours
                </p>
              </div>
            </section>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
};

export default CommunityGuidelines;
